import { ExclamationTriangleIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { OtpCodeInput } from '@/components/ui/otp-code-input';
import { mfaCodeSchema } from '@/utils/schemas';

const mfaSignInSchema = z.object({
  mfaCode: mfaCodeSchema,
});

type MfaSignInFormData = z.infer<typeof mfaSignInSchema>;

type CodeType = 'totp' | 'backup';

interface MfaSignInFormProps {
  // ログインしようとしているアカウント
  email: string;
  onSubmit: (mfaCode: string) => void;
  // メールアドレスとパスワードの入力に戻る
  onBack: () => void;
  isPending: boolean;
  // API が返したエラー(コード誤り・ロック中など)
  errorMessage?: string;
  // 認証アプリのコードとバックアップコードを切り替えた(前のコードのエラーを消すために使う)
  onCodeTypeChange: () => void;
}

/**
 * ログインの 2 段階目。認証アプリの6桁コードを入力する。
 * MFA はアニマルオフィスで設定するので、認証アプリにはアニマルオフィスの名前で登録されている。
 * 認証アプリを使えない場合はバックアップコードの入力に切り替えられる
 */
export const MfaSignInForm = ({
  email,
  onSubmit,
  onBack,
  isPending,
  errorMessage,
  onCodeTypeChange,
}: MfaSignInFormProps) => {
  const [codeType, setCodeType] = useState<CodeType>('totp');
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MfaSignInFormData>({
    resolver: zodResolver(mfaSignInSchema),
    defaultValues: { mfaCode: '' },
  });

  const submit = handleSubmit((data) => onSubmit(data.mfaCode));

  const switchCodeType = (next: CodeType) => {
    reset({ mfaCode: '' });
    setCodeType(next);
    onCodeTypeChange();
  };

  const codeError = errors.mfaCode?.message ?? errorMessage;

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      <p className="flex items-center gap-1.5 text-sm text-neutral-600">
        <UserCircleIcon className="size-5 shrink-0" />
        <span className="break-all">{email}</span>
      </p>
      {codeError && (
        <p role="alert" className="flex gap-2 font-bold text-red-700">
          <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" />
          {codeError}
        </p>
      )}

      {codeType === 'totp' ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="mfa-signin-code" className="font-bold">
              認証アプリの6桁のコード
            </label>
            <p className="text-sm text-neutral-600">
              スマートフォンの認証アプリで「アニマルオフィス」のコードを確認してください。アニマルEDIでも同じコードを使います。
            </p>
          </div>
          <div className="flex justify-center">
            <Controller
              name="mfaCode"
              control={control}
              render={({ field }) => (
                <OtpCodeInput
                  {...field}
                  id="mfa-signin-code"
                  // 6桁そろったら送信する。送信中の二重送信は防ぐ
                  onComplete={() => {
                    if (!isPending) submit();
                  }}
                  disabled={isPending}
                  invalid={!!codeError}
                  autoFocus
                />
              )}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="mfa-signin-backup-code" className="font-bold">
            バックアップコード
          </label>
          <p className="text-sm text-neutral-600">
            保管しておいたバックアップコードのうち、まだ使っていないものを1つ入力してください。
          </p>
          <input
            id="mfa-signin-backup-code"
            {...register('mfaCode')}
            type="text"
            autoComplete="off"
            autoCapitalize="off"
            placeholder="xxxxx-xxxxx"
            // biome-ignore lint/a11y/noAutofocus: 切り替えた直後にそのまま入力できるようにする
            autoFocus
            aria-invalid={codeError ? true : undefined}
            className="h-12 text-center font-mono text-lg! tracking-wider md:h-11"
          />
          <p className="text-sm text-neutral-600">
            使ったコードは再び使えません。アニマルオフィスの設定画面で再発行できます。
          </p>
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        fullWidth
        disabled={isPending}
        loading={isPending}
        className="text-base"
      >
        ログイン
      </Button>

      <div className="flex flex-col items-center gap-3 border-t border-neutral-200 pt-5">
        <button
          type="button"
          onClick={() => switchCodeType(codeType === 'totp' ? 'backup' : 'totp')}
          disabled={isPending}
          className="text-sm text-sky-800 underline hover:opacity-60 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {codeType === 'totp'
            ? 'スマートフォンを使えない場合（バックアップコード）'
            : '認証アプリのコードを使う'}
        </button>
        {/* 送信中に戻って別アカウントでログインすると、後から届いた応答でトークンが上書きされるため無効にする */}
        <button
          type="button"
          onClick={onBack}
          disabled={isPending}
          className="text-sm text-neutral-600 underline hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          メールアドレスとパスワードの入力に戻る
        </button>
      </div>
    </form>
  );
};
